import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, Building2, CalendarDays, ClipboardList,
  FileText, Inbox, Loader2, MailCheck, Receipt, Sparkles,
} from 'lucide-react';
import { dashboardApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useAppEvent } from '@/utils/appEvents';
import { PageContainer } from '@/components/shell/PageContainer';
import { formatCurrency, formatDateTime } from '@/utils/currency';
import { getEventTypeConfig } from '@/utils/eventTypes';
import type {
  DashboardData, DashboardFreigabeItem, DashboardMailIngestItem,
} from '@/types';

/** Dashboard: „Was ist zu tun?" — Rollen- und Modul-abhängige Aufgabenkarten. */
export function DashboardPage() {
  const { user, hasModule, isApprover, isVertrieb, role } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setIsLoading(true);
    setError('');
    dashboardApi
      .get()
      .then((response) => setData(response.data.data))
      .catch(() => setError('Dashboard konnte nicht geladen werden'))
      .finally(() => setIsLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  // Live-Refresh: Rechnungen (inkl. Mail-Eingang) und Bauvorhaben ändern
  // sich aus anderen Views heraus — die gemountet gehaltene Seite kann so
  // still nachladen.
  useAppEvent('rechnungen:changed', load);
  useAppEvent('bauvorhaben:changed', load);

  const firstName = (user?.name ?? '').split(' ')[0];
  const today = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(new Date());

  return (
    <PageContainer>
      {/* Kopf: Begrüßung + Datum */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {firstName ? `Guten Tag, ${firstName}` : 'Dashboard'}
          </h1>
          <p className="text-sm text-gray-500">{today}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : error && !data ? (
        <div className="card p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="mb-4 text-gray-700">{error}</p>
          <button onClick={load} className="btn btn-secondary">Erneut versuchen</button>
        </div>
      ) : data ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {/* Rechnungen — Buchhaltung (Büro-Rollen) */}
          {hasModule('rechnungen') && data.invoices?.scope === 'office' && (
            <DashboardCard
              title="Rechnungen"
              icon={<Receipt className="h-4 w-4" />}
              linkTo="/rechnungen"
              linkLabel="Zur Rechnungstabelle"
            >
              <TaskLink
                to="/rechnungen?status=neu_eingegangen"
                label="Neu eingegangen"
                hint="Aus dem Mail-Eingang — prüfen + zuordnen"
                count={data.invoices.counts.neu_eingegangen}
              />
              <TaskLink to="/rechnungen" label="Zur formalen Prüfung" hint="Erfasst" count={data.invoices.counts.erfasst} />
              <TaskLink to="/rechnungen" label="Korrigieren" hint="Korrektur erforderlich" count={data.invoices.counts.korrektur_erforderlich} />
              <TaskLink to="/rechnungen" label="Zur Lexware-Übergabe" hint="Freigegeben" count={data.invoices.counts.freigegeben} />
              <TaskLink
                to="/rechnungen?source=email"
                label="Ohne Bauvorhaben zuordnen"
                hint="Mail-Eingang ohne Zuordnung"
                count={data.invoices.counts.ohne_bauvorhaben}
                danger
              />
              <TaskLink
                to="/rechnungen"
                label="Überfällig"
                hint="Offen, Fälligkeit vorbei"
                count={data.invoices.counts.ueberfaellig}
                danger
              />
            </DashboardCard>
          )}

          {/* Rechnungen — Bauleiter: eigene Freigaben */}
          {hasModule('rechnungen') && data.invoices?.scope === 'bauleiter' && (
            <DashboardCard
              title="Rechnungen zur Freigabe"
              icon={<Receipt className="h-4 w-4" />}
              linkTo="/rechnungen"
              linkLabel="Zur Rechnungstabelle"
            >
              {data.invoices.zur_freigabe.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Rechnungen auf Ihren Bauvorhaben zur Freigabe.</p>
              ) : (
                <>
                  <ul className="divide-y divide-gray-100">
                    {data.invoices.zur_freigabe.map((inv) => (
                      <FreigabeItem key={inv.id} item={inv} />
                    ))}
                  </ul>
                  {data.invoices.zur_freigabe_count > data.invoices.zur_freigabe.length && (
                    <Link to="/rechnungen" className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                      Alle {data.invoices.zur_freigabe_count} anzeigen
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </>
              )}
            </DashboardCard>
          )}

          {/* Mail-Eingang (admin, Modul rechnungen — Key nur für admin) */}
          {hasModule('rechnungen') && data.mail_ingest && (
            <DashboardCard
              title="E-Mail-Eingang"
              icon={<MailCheck className="h-4 w-4" />}
              linkTo="/admin?tab=mail"
              linkLabel="Staging verwalten"
            >
              <p className="mb-2 text-sm text-gray-500">
                {data.mail_ingest.mailbox ?? 'rechnung@gpsbau.de'}
                {data.mail_ingest.letzte_abholung
                  ? ` · letzte Abholung ${formatDateTime(data.mail_ingest.letzte_abholung)}`
                  : ' · noch keine Abholung'}
              </p>
              {data.mail_ingest.letzter_fehler && (
                <p className="mb-2 flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {data.mail_ingest.letzter_fehler}
                </p>
              )}
              {data.mail_ingest.items.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Keine Anhänge ohne automatische Rechnung.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {data.mail_ingest.items.map((item) => (
                    <MailIngestRow key={item.id} item={item} />
                  ))}
                </ul>
              )}
            </DashboardCard>
          )}

          {/* KI-Extraktion — approver+ */}
          {isApprover && data.ki_extraktion && (
            <DashboardCard
              title="KI-Extraktion"
              icon={<Sparkles className="h-4 w-4" />}
              linkTo="/proposals"
              linkLabel="Zu den Anträgen"
            >
              <TaskLink
                to="/proposals"
                label="Datenblätter prüfen"
                hint="Extrahiert, Übernahme ausstehend"
                count={data.ki_extraktion.zur_pruefung}
              />
              <TaskLink
                to="/proposals"
                label="Fehlgeschlagene Jobs"
                count={data.ki_extraktion.fehlgeschlagen}
                danger
              />
              {data.ki_extraktion.fehler_items.length > 0 && (
                <ul className="mt-2 divide-y divide-gray-100">
                  {data.ki_extraktion.fehler_items.map((job) => (
                    <li key={job.id} className="py-1.5 text-sm">
                      {job.bauvorhaben_id && job.bauvorhaben_name ? (
                        <Link
                          to={`/bauvorhaben/${job.bauvorhaben_id}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {job.bauvorhaben_name}
                        </Link>
                      ) : (
                        <span className="text-gray-500">Job #{job.id}</span>
                      )}
                      <span className="block truncate text-xs text-red-700" title={job.error_message ?? undefined}>
                        {job.error_message ?? 'Fehler ohne Meldung'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </DashboardCard>
          )}

          {/* Meine Bauvorhaben — Bauleiter / Vertrieb */}
          {(role === 'bauleiter' || isVertrieb) && data.bauvorhaben && (
            <DashboardCard
              title={data.bauvorhaben.scope === 'vertrieb' ? 'Meine Bauvorhaben (Vertrieb)' : 'Meine Bauvorhaben'}
              icon={<Building2 className="h-4 w-4" />}
              linkTo="/bauvorhaben"
              linkLabel="Zu den Bauvorhaben"
            >
              {data.bauvorhaben.items.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {isVertrieb ? 'Ihnen sind keine Bauvorhaben zugeordnet.' : 'Sie sind keinem laufenden Bauvorhaben als Bauleiter zugeordnet.'}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {data.bauvorhaben.items.map((bv) => (
                    <BauvorhabenProgressRow key={bv.id} item={bv} />
                  ))}
                </ul>
              )}
            </DashboardCard>
          )}

          {/* Anträge — approver+ */}
          {isApprover && data.antraege && (
            <DashboardCard
              title="Anträge prüfen"
              icon={<ClipboardList className="h-4 w-4" />}
              linkTo="/proposals"
              linkLabel="Zu den Anträgen"
            >
              <TaskLink
                to="/proposals"
                label="Kontaktanträge"
                count={data.antraege.pending_contacts}
              />
              <TaskLink
                to="/proposals"
                label="Terminvorschläge"
                count={data.antraege.pending_events}
              />
            </DashboardCard>
          )}

          {/* Eigene pendende Anträge (alle Rollen) */}
          {!!data.my_pending_proposals && data.my_pending_proposals > 0 && (
            <DashboardCard title="Anträge" icon={<FileText className="h-4 w-4" />}>
              <TaskLink
                to="/proposals"
                label="In Prüfung"
                count={data.my_pending_proposals}
              />
            </DashboardCard>
          )}

          {/* Kalender — nächste 7 Tage */}
          {hasModule('kalender') && data.events && (
            <DashboardCard
              title="Termine (nächste 7 Tage)"
              icon={<CalendarDays className="h-4 w-4" />}
              linkTo="/calendar"
              linkLabel="Zum Kalender"
            >
              {data.events.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Termine in den nächsten 7 Tagen.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {data.events.map((ev) => {
                    const meta = getEventTypeConfig(ev.event_type);
                    return (
                      <li key={ev.id}>
                        <Link
                          to={`/calendar/${ev.id}`}
                          className="flex items-center gap-2 py-2 text-sm hover:bg-gray-50"
                        >
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${meta.color}`}>
                            {meta.label}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-gray-900">{ev.title}</span>
                          <span className="shrink-0 tabular-nums text-gray-500">
                            {formatEventDate(ev)}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </DashboardCard>
          )}

          {/* Nichts zu tun / keine Karten (Module deaktiviert) */}
          {!data.invoices && !data.mail_ingest && !data.ki_extraktion && !data.bauvorhaben
            && !data.antraege && !data.my_pending_proposals && !data.events && (
            <div className="card p-6 text-sm text-gray-500">
              Keine Aufgaben vorhanden.
            </div>
          )}
        </div>
      ) : null}
    </PageContainer>
  );
}

/** Aufgabenkarte: Titelzeile + Inhalt, optionale Fußzeile mit Link. */
function DashboardCard({ title, icon, children, linkTo, linkLabel }: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="card flex flex-col p-5">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900">
        <span className="text-gray-400">{icon}</span>
        {title}
      </h2>
      <div className="min-h-0 flex-1">{children}</div>
      {linkTo && linkLabel && (
        <Link
          to={linkTo}
          className="mt-3 inline-flex items-center gap-1 border-t border-gray-100 pt-3 text-sm text-primary-600 hover:text-primary-700"
        >
          {linkLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

/** Klickbarer Zähler-Zeileneintrag (Zahl rechts, optional rot bei Überfällig). */
function TaskLink({ to, label, hint, count, danger = false }: {
  to: string;
  label: string;
  hint?: string;
  count: number;
  danger?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-sm hover:bg-gray-50 ${danger && count > 0 ? 'text-red-700' : 'text-gray-900'}`}
    >
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5 font-medium">
          {danger && count > 0 && <AlertCircle className="h-4 w-4 shrink-0" />}
          {label}
        </span>
        {hint && <span className="text-xs text-gray-500">{hint}</span>}
      </span>
      <span
        className={`shrink-0 rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums ${
          count > 0
            ? danger ? 'bg-red-100 text-red-800' : 'bg-primary-100 text-primary-800'
            : 'bg-gray-100 text-gray-400'
        }`}
      >
        {count}
      </span>
    </Link>
  );
}

/** Freigabe-Eintrag (Bauleiter-Karte): Nummer, BV, Betrag, Datum. */
function FreigabeItem({ item }: { item: DashboardFreigabeItem }) {
  return (
    <li>
      <Link to={`/rechnungen/${item.id}`} className="flex items-center justify-between gap-2 py-2 text-sm hover:bg-gray-50">
        <span className="min-w-0 flex-1 truncate text-gray-900">
          <span className="font-medium">{item.invoice_number}</span>
          {item.bauvorhaben_name && <span className="text-gray-500"> · {item.bauvorhaben_name}</span>}
        </span>
        <span className="shrink-0 text-right tabular-nums">
          <span className="font-medium text-gray-900">{formatCurrency(item.amount_gross)}</span>
          <span className="block text-xs text-gray-500">{formatDate(item.invoice_date)}</span>
        </span>
      </Link>
    </li>
  );
}

/** Staging-Zeile (E-Mail-Eingang-Karte): Anhang + Status/Begründung. */
function MailIngestRow({ item }: { item: DashboardMailIngestItem }) {
  return (
    <li className="py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-gray-900">
          <Inbox className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="min-w-0 truncate">
            <span className="font-medium">{item.original_name ?? 'Anhang'}</span>
            {item.sender_email && <span className="text-gray-500"> · {item.sender_email}</span>}
          </span>
        </span>
        <span className="shrink-0 text-xs text-gray-500">
          {item.received_at_berlin ? formatDate(item.received_at_berlin) : ''}
        </span>
      </div>
      {(item.skip_reason || item.error_message) && (
        <span className="mt-0.5 block truncate text-xs text-red-700" title={item.skip_reason ?? item.error_message ?? undefined}>
          {item.skip_reason ?? item.error_message}
        </span>
      )}
    </li>
  );
}

/** Bauvorhaben-Fortschritt (Meine Bauvorhaben-Karte). */
function BauvorhabenProgressRow({ item }: { item: DashboardBauvorhabenRow }) {
  return (
    <li className="py-2.5">
      <Link to={`/bauvorhaben/${item.id}`} className="block hover:opacity-80">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="min-w-0 truncate font-medium text-gray-900">{item.name}</span>
          <span className="shrink-0 text-xs text-gray-500">
            {item.gewerke_offen > 0 ? `${item.gewerke_offen} Gewerke offen` : 'Gewerke vollständig'}
            {' · '}
            {item.vertragsunterlagen_offen > 0 ? `${item.vertragsunterlagen_offen} Unterlagen offen` : 'Unterlagen vollständig'}
          </span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-primary-600"
            style={{ width: `${item.progress_percent}%` }}
          />
        </div>
        <div className="mt-0.5 text-xs text-gray-500">{item.progress_percent} % erledigt</div>
      </Link>
    </li>
  );
}

type DashboardBauvorhabenRow = NonNullable<DashboardData['bauvorhaben']>['items'][number];

/** Datum aus DATETIME-Wireformat ("YYYY-MM-DD …" → dd.MM. [+ HH:MM]). */
function formatDate(value: string): string {
  return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
}

function formatEventDate(ev: { start_date: string; end_date?: string | null; is_all_day: boolean }): string {
  let text = formatDate(ev.start_date);
  if (!ev.is_all_day) text += ` ${ev.start_date.slice(11, 16)}`.trim();
  // Mehrtagestermin: Enddatum anhängen (Ende 23:59:59 inklusiv).
  if (ev.end_date && !ev.end_date.startsWith(ev.start_date.slice(0, 10))) {
    text += ` – ${formatDate(ev.end_date)}`;
  }
  return text;
}